<script setup lang="ts">
import { RouterView, useRoute, useRouter, type RouteRecordNameGeneric } from 'vue-router'
import { onBeforeMount, onErrorCaptured, onMounted, ref, watch } from 'vue'
import { GlobalError } from './utils/globalError/globalError'
import { useMiiaFwStore } from '@/stores/miiaFwStore'
import { storeToRefs } from 'pinia'
import MiiaFwConfig from '@/configs/config'
import { useLoadingView } from './features/miiaFw/composables/useLoadingView'
import { useUserAttributes } from './features/miiaFw/composables/useUserAttributes'
import { isAuthenticated } from './features/miiaFw/utils/miiaFwCognito'
import { useCommonModalStore } from './stores/commonModalStore'
import ViewLoading from './features/miiaFw/components/ViewLoading.vue'
import CommonHeader from './components/header/PageHeader.vue'
import CommonModal from '@/components/common/dialog/CommonModal.vue'
import AerialImageAnnotationModal from '@/components/annotation/AerialImageAnnotationModal.vue'
import RecertificationDialog from '@/features/miiaFw/components/RecertificationDialog.vue'

const router = useRouter()
const route = useRoute()

const miiaFwStore = useMiiaFwStore()
const { isAuthorized } = storeToRefs(miiaFwStore)

const { setLoadingView } = useLoadingView()
const { getAttributes } = useUserAttributes()

const commonModalStore = useCommonModalStore()

// アプリコンテンツ表示可能状態フラグ
const isReady = ref(false)

const view = ref<RouteRecordNameGeneric>('')

onBeforeMount(() => {
  // 未認証状態でも表示可能なメニュー項目を設定
  miiaFwStore.updateMenu(
    router.getRoutes().filter(item => item.meta.onMenu && !item.meta.requiresAuth)
  )
  if (MiiaFwConfig.config.authorization.mustAuth) {
    // 認証必須システム
    if (isAuthorized.value) {
      // 認証済
    } else {
      // 未認証
      // トークン取得処理
      setLoadingView(true)
      isAuthenticated()
        .then(() => {
          // 未認証から認証中へ変化した場合と認証中にリフレッシュした場合は必ずisAuthorizedがfalse → trueに変化し、getInfo内の処理が実行される。
          // 認証中であった場合の処理中解除はgetInfo()処理内で実施
          // リフレッシュ後に認証済であった場合に実施する処理(リフレッシュなしで認証した場合は通らない。)
        })
        .catch(() => {
          if (route.meta.requiresAuth && router.currentRoute.value.path !== '/') {
            router.push({ path: '/' }) // 先にトップ画面に戻さないと表示されてしまう。
          }
          setLoadingView(false) // 同期処理終了後に必ず実行
          isReady.value = true
        })
    }
  }
  // 現在の画面を設定
  view.value = route.name
})

onErrorCaptured(err => {
  if (err instanceof GlobalError) {
    err.showErrorMessage()
  } else {
    // コンソールには表示させておく
    console.error(err)
  }
})
onMounted(() => {
  // 残りの同期処理のエラーをキャッチ
  window.addEventListener('error', event => {
    if (event.error instanceof GlobalError) {
      event.error.showErrorMessage()
    }
  })
  // 残りの非同期処理のエラーをキャッチ
  window.addEventListener('unhandledrejection', event => {
    if (event.reason instanceof GlobalError) {
      // イベントを抑止する
      event.preventDefault()
      event.reason.showErrorMessage()
    }
  })
})

watch(isAuthorized, newVal => {
  if (newVal === true) {
    // 未認証から認証中へ変化した場合と認証中にリフレッシュした場合
    getAttributes()
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      .then((user: any) => {
        // 認証が必要なメニューも含めて表示
        miiaFwStore.updateMenu(
          router.getRoutes().filter(value => {
            if (
              Object.prototype.hasOwnProperty.call(value.meta, 'accessibleRoles') &&
              Array.isArray(value.meta.accessibleRoles)
            ) {
              if (
                !Object.prototype.hasOwnProperty.call(user, 'attribute') ||
                !Object.prototype.hasOwnProperty.call(user.attribute, MiiaFwConfig.config.sysId)
              ) {
                return false
              }
              const userAttributes = user.attribute[MiiaFwConfig.config.sysId]
              if (Object.prototype.hasOwnProperty.call(userAttributes, 'role')) {
                return (
                  value.meta.accessibleRoles.indexOf(userAttributes.role) > -1 && value.meta.onMenu
                )
              } else {
                return false
              }
            } else {
              return value.meta.onMenu
            }
          })
        )
        miiaFwStore.updateSensitiveInfo({})
        setLoadingView(false)
      })
      .catch(error => {
        setLoadingView(false)
        commonModalStore.openAlertModal({ body: error.message.errorMessage })
      })
  } else {
    // 未認証状態でも表示可能なメニュー項目を設定
    miiaFwStore.updateMenu(
      router.getRoutes().filter(item => item.meta.onMenu && !item.meta.requiresAuth)
    )
  }
  isReady.value = true
})
</script>

<template>
  <ViewLoading v-if="miiaFwStore.isLoading" :style="{ zIndex: isReady ? undefined : 1049 }" />
  <div v-if="isReady" class="contents">
    <CommonHeader />
    <RouterView />
  </div>
  <CommonModal />
  <RecertificationDialog />
  <AerialImageAnnotationModal />
</template>

<style scoped>
header {
  line-height: 1.5;
  max-height: 100vh;
}

.logo {
  display: block;
  margin: 0 auto 2rem;
}

.contents {
  height: 100vh;
  overflow: hidden;
}

nav {
  width: 100%;
  font-size: 12px;
  text-align: center;
  margin-top: 2rem;
}

nav a.router-link-exact-active {
  color: var(--color-text);
}

nav a.router-link-exact-active:hover {
  background-color: transparent;
}

nav a {
  display: inline-block;
  padding: 0 1rem;
  border-left: 1px solid var(--color-border);
}

nav a:first-of-type {
  border: 0;
}

@media (min-width: 1024px) {
  header {
    display: flex;
    place-items: center;
  }

  .logo {
    margin: 0 2rem 0 0;
  }

  header .wrapper {
    display: flex;
    place-items: flex-start;
    flex-wrap: wrap;
  }

  nav {
    text-align: left;
    margin-left: -1rem;
    font-size: 1rem;

    padding: 1rem 0;
    margin-top: 1rem;
  }
}
</style>
